import { useState } from 'react'
import {
  ActionIcon,
  Button,
  Group,
  Indicator,
  Popover,
  ScrollArea,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core'
import { IconBell } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import type { NotificationDto } from '@ledokol/contracts'
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../../shared/api/notifications'
import { TaskModal } from '../task-modal/task-modal'

/**
 * Колокольчик в хедере: поллинг каждые 30с (websocket для LAN-масштаба не нужен),
 * Popover-лента последних 30, клик по строке = прочитано + открыть task-modal.
 */
export function NotificationBell() {
  const qc = useQueryClient()
  const [opened, setOpened] = useState(false)
  const [openTaskId, setOpenTaskId] = useState<string | null>(null)

  const q = useQuery({
    queryKey: ['notifications'],
    queryFn: fetchNotifications,
    refetchInterval: 30_000,
  })
  const unread = q.data?.unread_count ?? 0

  const readOne = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })
  const readAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })

  function onItemClick(n: NotificationDto) {
    if (!n.read_at) readOne.mutate(n.id)
    if (n.task) {
      setOpened(false)
      setOpenTaskId(n.task.id)
    }
  }

  return (
    <>
      <Popover
        width={380}
        position="bottom-end"
        shadow="md"
        opened={opened}
        onChange={setOpened}
      >
        <Popover.Target>
          <Indicator
            label={unread > 99 ? '99+' : unread}
            size={16}
            color="red"
            disabled={unread === 0}
            offset={4}
          >
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              onClick={() => setOpened((o) => !o)}
              title="Уведомления"
            >
              <IconBell size={20} stroke={1.6} />
            </ActionIcon>
          </Indicator>
        </Popover.Target>
        <Popover.Dropdown p="xs">
          <Group justify="space-between" mb="xs" px={4}>
            <Text fw={700} size="sm">
              Уведомления
            </Text>
            {unread > 0 && (
              <Button
                variant="subtle"
                size="compact-xs"
                onClick={() => readAll.mutate()}
                loading={readAll.isPending}
              >
                Прочитать все
              </Button>
            )}
          </Group>
          {(!q.data || q.data.items.length === 0) && (
            <Text size="sm" c="dimmed" ta="center" py="md">
              Уведомлений пока нет
            </Text>
          )}
          {q.data && q.data.items.length > 0 && (
            <ScrollArea.Autosize mah={420}>
              <Stack gap={2}>
                {q.data.items.map((n) => (
                  <UnstyledButton
                    key={n.id}
                    onClick={() => onItemClick(n)}
                    px={6}
                    py={6}
                    style={{
                      borderRadius: 6,
                      background: n.read_at ? undefined : 'var(--mantine-color-blue-0)',
                    }}
                  >
                    <Group gap={8} wrap="nowrap" align="flex-start">
                      {!n.read_at && (
                        <span
                          style={{
                            width: 8, height: 8, borderRadius: 4, marginTop: 6,
                            background: 'var(--mantine-color-blue-6)', flexShrink: 0,
                          }}
                        />
                      )}
                      <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" lineClamp={2}>
                          {lineFor(n)}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {dayjs(n.created_at).format('D MMM HH:mm')}
                        </Text>
                      </Stack>
                    </Group>
                  </UnstyledButton>
                ))}
              </Stack>
            </ScrollArea.Autosize>
          )}
        </Popover.Dropdown>
      </Popover>

      <TaskModal taskId={openTaskId} onClose={() => setOpenTaskId(null)} onOpenTask={setOpenTaskId} />
    </>
  )
}

function lineFor(n: NotificationDto): string {
  const who = n.actor?.full_name ?? 'Кто-то'
  const title = n.task ? `«${n.task.title}»` : 'задаче'
  switch (n.kind) {
    case 'task_assigned':
      return `${who} назначил(а) вам задачу ${title}`
    case 'comment_added':
      return `${who} прокомментировал(а) ${title}`
    case 'mentioned':
      return `${who} упомянул(а) вас в ${title}`
    case 'task_due_soon':
      return `Дедлайн в ближайшие 24 часа: ${title}`
    case 'task_overdue':
      return `Задача просрочена: ${title}`
    case 'task_completed':
      return `Задача выполнена: ${title}`
    case 'draft_for_confirmation':
      return `Черновик из Telegram ждёт подтверждения`
    case 'recurring_created':
      return `Создана регулярная задача ${title}`
    default:
      return n.kind
  }
}
